import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import classNames from 'classnames';
import { Fragment, useMemo, useState } from 'react';
import { Checkbox } from '../Checkbox/Checkbox';
import { Chip } from '../Chip/Chip';
import { Icon } from '../Icon/Icon';
import { getSelectClassNames } from '../Select/Select.styles';
import {
  type SelectOption,
  isSelectOptionObject,
} from '../Select/Select.types';
import { SELECT_ALL, useSelectList } from '../Select/useSelectList';
import { TextInput } from '../TextInput/TextInput';
import { useStableCallback } from '../hooks/useStableCallback';
import { ENTER, SPACE_KEY } from '../utils/keyboard/keyboardKeys';
import type { MultiSelectProps } from './MultiSelect.types';

export const MultiSelect = <TValue extends string>(
  props: MultiSelectProps<TValue>,
) => {
  const {
    className,
    clearable,
    name,
    onChange,
    options = [],
    value,
    ...restProps
  } = props;

  const selectClassNames = getSelectClassNames();

  const [allOptionsChecked, setAllOptionsChecked] = useState(false);

  const {
    activeIndex,
    context,
    floatingStyles,
    placement,
    getFloatingProps,
    getItemProps,
    getOptionLabel,
    getReferenceProps,
    isTypingRef,
    listRef,
    menuOpen,
    refs,
    setSelectedIndex,
  } = useSelectList(options, value, props.disabled);

  const optionsMap = useMemo(() => {
    return options.reduce<Record<TValue, SelectOption<TValue>>>(
      (acc, option) => {
        acc[isSelectOptionObject(option) ? option.value : option] = option;
        return acc;
      },
      {} as Record<TValue, SelectOption<TValue>>,
    );
  }, [options]);

  const handleSelect = (
    option: SelectOption<TValue> | typeof SELECT_ALL,
    index: number,
  ) => {
    setSelectedIndex(index);

    const selectedValues = Array.isArray(value) ? value : [];

    if (option === SELECT_ALL) {
      setAllOptionsChecked(!allOptionsChecked);
      onChange(
        allOptionsChecked
          ? []
          : options.map((option) => getOptionValue(option)),
      );
      return;
    }

    const optionValue = getOptionValue(option);

    const newValues = isOptionInValueArray(option, selectedValues)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];

    setAllOptionsChecked(
      options.every((opt) => newValues.includes(getOptionValue(opt))),
    );

    onChange(newValues);
  };

  const isOptionEqualToValueInternal = useStableCallback(
    (option: SelectOption<TValue>, value: string): boolean => {
      if (isSelectOptionObject(option)) {
        return option.value === value;
      }
      return option === value;
    },
  );

  const isOptionInValueArray = (
    option: SelectOption<TValue>,
    valueArray: TValue[],
  ): boolean => {
    return valueArray.some((val) => isOptionEqualToValueInternal(option, val));
  };

  const getOptionValue = useStableCallback(
    (option: SelectOption<TValue>): TValue => {
      if (isSelectOptionObject(option)) {
        return option.value;
      }
      return option;
    },
  );

  const toggleAllOptions = useStableCallback(() => {
    setAllOptionsChecked((prev) => !prev);

    onChange(
      allOptionsChecked ? [] : options.map((option) => getOptionValue(option)),
    );
  });

  const handleClear = useStableCallback(() => {
    setAllOptionsChecked(false);
    onChange([]);
  });

  const dropdownPlacement = placement === 'top-start' ? 'top' : 'bottom';

  return (
    <div>
      <div ref={refs.setReference} {...getReferenceProps()}>
        <TextInput
          hasDropdown
          dropdownOpened={menuOpen ? dropdownPlacement : null}
          name={name}
          type='text'
          readOnly
          selectionDisabled
          value=''
          onClear={clearable ? handleClear : undefined}
          renderTags={
            Array.isArray(value) && value.length > 0
              ? () =>
                  value.map((item, i) => {
                    const label = getOptionLabel(optionsMap[item]);
                    return (
                      <Chip
                        variant='outline'
                        // Values are unique; labels are missing until options load.
                        key={String(item)}
                        size={props.size}
                        onRemove={() => handleSelect(item, i)}
                        disabled={props.disabled}
                      >
                        {label}
                      </Chip>
                    );
                  })
              : undefined
          }
          endAdornment={
            <Icon
              className={classNames(
                'text-default',
                props.disabled
                  ? 'text-status-disabled-dark cursor-default'
                  : 'cursor-pointer',
              )}
              glyph={menuOpen ? 'fa-chevron-up' : 'fa-chevron-down'}
              iconStyle='regular'
              onMouseDown={(e) => e.preventDefault()}
            />
          }
          id={props.inputId}
          {...restProps}
        />
      </div>
      {menuOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              data-testid='multi-select-options'
              className={classNames(
                selectClassNames.optionsContainer,
                dropdownPlacement === 'top'
                  ? selectClassNames.optionContainerTop
                  : selectClassNames.optionContainerBottom,
              )}
              style={{
                ...floatingStyles,
              }}
              {...getFloatingProps()}
            >
              {([SELECT_ALL, ...options] as const).map((option, i) => {
                const label = getOptionLabel(option);
                const selectAllOption = option === SELECT_ALL;

                const handleThisItemSelect = selectAllOption
                  ? toggleAllOptions
                  : () => handleSelect(option, i);

                const checkboxChecked = selectAllOption
                  ? allOptionsChecked
                  : isOptionInValueArray(
                      option,
                      Array.isArray(value) ? value : [],
                    );

                return (
                  <Fragment key={label}>
                    <div
                      ref={(node) => {
                        listRef.current[i] = node;
                      }}
                      tabIndex={i === activeIndex ? 0 : -1}
                      aria-selected={checkboxChecked}
                      className={classNames(
                        selectClassNames.option,
                        i === activeIndex && 'bg-state-hover',
                      )}
                      {...getItemProps({
                        // Handle pointer select.
                        onClick() {
                          handleThisItemSelect();
                        },
                        // Handle keyboard select.
                        onKeyDown(event) {
                          if (
                            event.key === ENTER ||
                            (event.key === SPACE_KEY && !isTypingRef.current)
                          ) {
                            event.preventDefault();
                            handleThisItemSelect();
                          }
                        },
                      })}
                    >
                      <Checkbox value={checkboxChecked} />
                      {label}
                    </div>
                    {selectAllOption && (
                      <div className='pt-xs mt-xs border-t border-divider' />
                    )}
                  </Fragment>
                );
              })}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </div>
  );
};
